import React, { createContext, useCallback, useContext, useRef } from "react";

/**
 * Permite que algo FORA da caixa de digitação ponha texto nela — hoje, um
 * plugin que desenha botões na bolha e, ao clique, oferece aquele texto ao
 * atendente (que confere e envia).
 *
 * Por que um contexto e não um callback descendo por props: quem escreve
 * (`MessagesList`) e quem recebe (`MessageInput`) são irmãos, não pai e filho.
 * É o mesmo arranjo do `ReplyMessageContext`, que já liga os dois.
 *
 * Por que inserir em vez de enviar: enviar no clique transforma um toque
 * acidental num balão antigo do histórico em mensagem para o cliente. O
 * atendente confere e aperta Enter.
 *
 * O registro é por `ref`, não por estado: a caixa registra sua função uma vez
 * ao montar, e a lista chamar isso não redesenha a conversa.
 */
const DraftInsertContext = createContext({
  inserirNoRascunho: () => {},
  registrarInsercao: () => () => {}
});

const DraftInsertProvider = ({ children }) => {
  const handler = useRef(null);

  /** A caixa de digitação se registra; o retorno desfaz o registro. */
  const registrarInsercao = useCallback(fn => {
    handler.current = typeof fn === "function" ? fn : null;
    return () => {
      if (handler.current === fn) handler.current = null;
    };
  }, []);

  /** Chamado de fora. Sem caixa montada (ticket fechado, por exemplo), não faz nada. */
  const inserirNoRascunho = useCallback(texto => {
    if (handler.current && texto) handler.current(String(texto));
  }, []);

  return (
    <DraftInsertContext.Provider value={{ inserirNoRascunho, registrarInsercao }}>
      {children}
    </DraftInsertContext.Provider>
  );
};

const useDraftInsert = () => useContext(DraftInsertContext);

export { DraftInsertContext, DraftInsertProvider, useDraftInsert };
